import React, { useState } from "react";
import { CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";
import * as Collapsible from "@radix-ui/react-collapsible";
import {
	QuickstartSection as QuickstartSectionType,
	QuickStartTaskId,
} from "../quickstart-tasks";
import { QuickstartTask } from "./QuickstartTask";
import {
	QuickStartAttributeValue,
	quickStartTaskIsCompleted,
} from "@/dentlab/src/store/Attributes";
import { cn } from "@/dentlab/src/lib/utils/cn";

interface QuickstartSectionProps {
	section: QuickstartSectionType;
	taskStatuses: Record<QuickStartTaskId, QuickStartAttributeValue>;
	onCtaClick?: (to: string) => void;
}

export const QuickstartSection: React.FC<QuickstartSectionProps> = ({
	section,
	taskStatuses,
	onCtaClick,
}) => {
	const completedCount = section.tasks.filter((task) => {
		const status = taskStatuses[task.id];
		return quickStartTaskIsCompleted(status);
	}).length;
	const totalCount = section.tasks.length;
	const allComplete = completedCount === totalCount;

	const [isOpen, setIsOpen] = useState(!allComplete);

	return (
		<Collapsible.Root
			open={isOpen}
			onOpenChange={setIsOpen}
			className="bg-white rounded-lg mb-4"
		>
			<Collapsible.Trigger
				className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
				data-testid={`quickstart-section-${section.id}`}
			>
				<div className="text-left">
					<div className="flex items-center gap-2">
						{allComplete && (
							<CheckCircle2 className="w-5 h-5 text-emerald-600" />
						)}
						<h3
							className={cn(
								"text-lg font-semibold",
								allComplete
									? "text-emerald-700"
									: "text-gray-900"
							)}
						>
							{section.title}
						</h3>
					</div>
					<p
						className={cn(
							"text-sm",
							allComplete ? "text-emerald-600" : "text-gray-600"
						)}
					>
						{completedCount} aus {totalCount} abgeschlossen
					</p>
				</div>
				<div className="flex-shrink-0">
					{isOpen ? (
						<ChevronDown className="w-5 h-5 text-gray-400" />
					) : (
						<ChevronRight className="w-5 h-5 text-gray-400" />
					)}
				</div>
			</Collapsible.Trigger>
			<Collapsible.Content>
				<div className="border-t border-gray-200">
					{section.tasks.map((task) => (
						<QuickstartTask
							key={task.id}
							task={task}
							status={taskStatuses[task.id] || "open"}
							onCtaClick={onCtaClick}
						/>
					))}
				</div>
			</Collapsible.Content>
		</Collapsible.Root>
	);
};
